import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { Trans } from "@lingui/react/macro";
import { AccessRestrictedPage } from "@/components/auth/AccessRestrictedPage";

const unauthorizedSearchSchema = z.object({
  reason: z.string().optional(),
  from: z.string().optional(),
  requiredRole: z.string().optional(),
  requiredPermission: z.string().optional(),
});

export const Route = createFileRoute("/(protected)/_layout/unauthorized")({
  validateSearch: (search) => unauthorizedSearchSchema.parse(search),
  component: UnauthorizedPage,
});

function UnauthorizedPage() {
  const search = Route.useSearch();

  // T-105: the required permission/role are developer detail. They used to be
  // rendered as "Details: Required permission: material:update", which told the
  // user nothing actionable. Keep them, but in the console only.
  useEffect(() => {
    if (!search.requiredPermission && !search.requiredRole && !search.from) return;
    console.warn('[yeschef] access restricted', {
      reason: search.reason ?? '(unspecified)',
      requiredPermission: search.requiredPermission ?? '(none)',
      requiredRole: search.requiredRole ?? '(none)',
      from: search.from ?? '(none)',
    });
  }, [search.reason, search.requiredPermission, search.requiredRole, search.from]);

  // "super admin" and "support" are internal Yes, Chef roles — a customer cannot
  // become one or reliably reach one, so these read as an internal restriction
  // rather than naming a role the reader is meant to go find.
  const details =
    search.reason === "internal_only" ||
    search.reason === "super_admin_only" ||
    search.reason === "support_only"
      ? <Trans>This section is only accessible to internal Yes, Chef users.</Trans>
      // Not a permission problem: the reader's role is fine, their organization
      // simply does not run this part of the product (T-1106). Telling them to
      // ask for access would send them somewhere that cannot help.
      : search.reason === "feature_unavailable"
        ? <Trans>This part of Yes, Chef is not turned on for your organization. Contact your organization owner if you need it.</Trans>
      : search.reason === "permission_denied"
        ? <Trans>You do not have the required permission to access this page. Contact your organization owner to request access.</Trans>
        : search.reason === "impersonation_role_denied"
          ? <Trans>Your role is not allowed to use impersonation.</Trans>
          : search.reason === "invalid_impersonation_target"
            ? <Trans>The selected impersonation target is invalid.</Trans>
            : <Trans>You do not have permission to access this page. Contact your organization owner to request access.</Trans>;

  return <AccessRestrictedPage description={details} />;
}
